/**
 * @fileoverview Joyfill Formulas Engine - Public API
 * 
 * A dependency-aware formula evaluation system for JoyDoc forms.
 * Supports spreadsheet-style formulas with automatic dependency resolution,
 * circular dependency detection, and sandboxed evaluation.
 * 
 * @example
 * import { resolveJoyDoc, formulas } from './formulas';
 * 
 * // Direct usage
 * const result = resolveJoyDoc(document);
 * 
 * // Convenience API (with status)
 * const result = formulas.resolve(document);
 */

// Core functionality
export { resolveJoyDoc } from './resolver.js';

// Error classes for instanceof checks
export {
  CircularDependencyError,
  UnknownFieldError,
  FormulaEvalError,
  UnknownFunctionError,
  UndefinedVariableError,
} from './errors.js';

// Built-in functions and metadata
export { builtins, BUILTIN_NAMES } from './builtins.js';

// Internal modules (exported for testing and advanced usage)
export { buildGraph } from './depGraph.js';
export { evalFormula } from './evaluator.js';

// Import resolver for convenience API
import { resolveJoyDoc } from './resolver.js';
 
/**
 * Convenience API that matches the pattern used by other Joyfill utilities
 * Provides additional status information and consistent error handling
 */
export const formulas = {
  /**
   * Resolves all formulas in a JoyDoc with enhanced status reporting
   * 
   * @param {Object} document - JoyDoc to process
   * @param {Object} [options={}] - Reserved for future configuration options
   * @returns {Object} Resolution result with status
   * @returns {Object} returns.doc - Updated document with resolved values
   * @returns {Array} returns.warnings - Array of warnings/errors encountered
   * @returns {string} returns.status - 'valid', 'warning', or 'error'
   * 
   * @example
   * const result = formulas.resolve(myDocument);
   * if (result.status === 'valid') {
   *   console.log('All formulas resolved successfully');
   * } else {
   *   console.warn('Some formulas had issues:', result.warnings);
   * }
   */
  resolve(document, options = {}) {
    try {
      return resolveJoyDoc(document);
    } catch (error) {
      return {
        doc: document,
        warnings: [error],
        status: 'error'
      };
    }
  },

  /**
   * Gets information about available built-in functions
   * 
   * @returns {Object} Information about built-ins
   * @returns {Object} returns.functions - Object containing all built-in functions
   * @returns {Set} returns.names - Set of function names
   * @returns {Array} returns.categories - Functions grouped by category
   */
  async getBuiltins() {
    const { builtins, BUILTIN_NAMES } = await import('./builtins.js');
    
    return {
      functions: builtins,
      names: BUILTIN_NAMES,
      categories: {
        math: ['sum', 'average', 'min', 'max', 'round', 'abs'],
        array: ['count', 'filter', 'map', 'some', 'every'],
        logic: ['iff']
      }
    };
  }
};